import { Box, Button, Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { MessageStatus } from "../../shared/AttendanceAPI";

/** One row of the table; Students and Staff both map their data to this. */
export interface MessageStatusRow {
  id: number;
  /** Admission No / Staff Id */
  code: string;
  name: string;
  attendance: string;
  messageStatus: MessageStatus;
  failureReason?: string;
  attemptCount: number;
  lastAttemptAt?: string;
}

const STATUS_CHIP: Record<MessageStatus, { label: string; color: "default" | "info" | "success" | "error" }> = {
  PENDING: { label: "Pending", color: "info" },
  SENT: { label: "Sent", color: "success" },
  FAILED: { label: "Failed", color: "error" },
  SKIPPED: { label: "Skipped", color: "default" },
};

type Props = {
  rows: MessageStatusRow[];
  /** Header of the code column, e.g. "Admission No" */
  codeLabel: string;
  /** When set, Retry is disabled and this is shown as the reason (e.g. not today, sending off) */
  retryDisabledReason?: string;
  /** Omit to hide retry entirely */
  onRetry?: (ids: number[]) => Promise<void>;
  emptyMessage: string;
};

/**
 * Message Status table shared by the Students and Staff attendance pages. Only FAILED rows can be
 * selected for retry; the backend enforces the same rules (FAILED only, today only, sending on).
 */
export const AttendanceMessageStatus = ({ rows, codeLabel, retryDisabledReason, onRetry, emptyMessage }: Props) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [retrying, setRetrying] = useState(false);

  const failedIds = useMemo(() => rows.filter((r) => r.messageStatus === "FAILED").map((r) => r.id), [rows]);
  const canRetry = !!onRetry && !retryDisabledReason;

  // keep only selections that are still FAILED after a refresh
  useEffect(() => {
    setSelected((prev) => prev.filter((id) => failedIds.includes(id)));
  }, [failedIds]);

  const toggle = (id: number) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const retry = async (ids: number[]) => {
    if (!onRetry || ids.length === 0) return;
    setRetrying(true);
    try {
      await onRetry(ids);
      setSelected([]);
    } finally {
      setRetrying(false);
    }
  };

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      {onRetry && (
        <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <Tooltip title={retryDisabledReason ?? ""}>
            <span>
              <Button size="small" variant="contained" disabled={!canRetry || retrying || selected.length === 0} onClick={() => retry(selected)}>
                Retry selected ({selected.length})
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={retryDisabledReason ?? ""}>
            <span>
              <Button size="small" variant="outlined" disabled={!canRetry || retrying || failedIds.length === 0} onClick={() => retry(failedIds)}>
                Retry all failed ({failedIds.length})
              </Button>
            </span>
          </Tooltip>
          {retryDisabledReason && (
            <Typography variant="caption" color="text.secondary">
              {retryDisabledReason}
            </Typography>
          )}
        </Box>
      )}
      <TableContainer className="mui-table-container-style">
        <Table size="small" className="mui-table-bordered">
          <TableHead>
            <TableRow>
              {onRetry && <TableCell padding="checkbox" />}
              <TableCell>{codeLabel}</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Attendance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Attempts</TableCell>
              <TableCell>Last attempt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                {onRetry && (
                  <TableCell padding="checkbox">
                    {row.messageStatus === "FAILED" && (
                      <Checkbox size="small" disabled={!canRetry} checked={selected.includes(row.id)} onChange={() => toggle(row.id)} />
                    )}
                  </TableCell>
                )}
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.attendance}</TableCell>
                <TableCell>
                  <Chip size="small" label={STATUS_CHIP[row.messageStatus].label} color={STATUS_CHIP[row.messageStatus].color} />
                </TableCell>
                <TableCell sx={{ maxWidth: 280, wordBreak: "break-word" }}>{row.failureReason ?? ""}</TableCell>
                <TableCell>{row.attemptCount}</TableCell>
                <TableCell>{row.lastAttemptAt ? dayjs(row.lastAttemptAt).format("DD-MMM-YYYY hh:mm A") : ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
